import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MobileShell from "@/components/MobileShell";
import AdminDisplayPreference, { adminShellClassName, useAdminDisplayPreference } from "@/components/AdminDisplayPreference";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ExerciseSharingButtons from "@/components/ExerciseSharingButtons";
import AdminHomeButton from "@/components/AdminHomeButton";
import { OptionSelect } from "@/components/ExerciseOptions";
import { toast } from "sonner";
import { Archive, ArrowLeft, ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";

const LEVELS = ["Basic", "Intermediate", "Advanced", "Return to Sport", "Other"];

const textValue = (value) => String(value ?? "").trim();

export default function AdminPlaylists() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [playlists, setPlaylists] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [options, setOptions] = useState({});
  const [selectedPlaylistId, setSelectedPlaylistId] = useState("");
  const [filters, setFilters] = useState({ q: "", condition: "all", level: "all" });
  const [form, setForm] = useState(getEmptyPlaylistForm());
  const [exerciseId, setExerciseId] = useState("");
  const [saving, setSaving] = useState(false);
  const [displayPreference, setDisplayPreference] = useAdminDisplayPreference();

  const selectedPlaylist = useMemo(
    () => playlists.find((playlist) => playlist.id === selectedPlaylistId) || null,
    [playlists, selectedPlaylistId],
  );

  const filteredExercises = useMemo(() => {
    return exercises.filter((exercise) => !selectedPlaylist?.exercises?.some((row) => row.exercise_id === exercise.id));
  }, [exercises, selectedPlaylist]);

  const loadPlaylists = useCallback(async () => {
    const params = {};
    if (filters.q.trim()) params.q = filters.q.trim();
    if (filters.condition !== "all") params.condition = filters.condition;
    if (filters.level !== "all") params.level = filters.level;
    const { data } = await api.get("/admin/playlists", { params });
    setPlaylists(data);
  }, [filters]);

  const loadAll = useCallback(async () => {
    try {
      const [playlistsRes, exercisesRes, optionsRes] = await Promise.all([
        api.get("/admin/playlists"),
        api.get("/admin/exercises"),
        api.get("/admin/exercise-options"),
      ]);
      setPlaylists(playlistsRes.data);
      setExercises(exercisesRes.data.filter((exercise) => exercise.is_active));
      setOptions(optionsRes.data);
      if (playlistsRes.data.length) setSelectedPlaylistId(playlistsRes.data[0].id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Unable to load playlists.");
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    loadPlaylists().catch(() => {});
  }, [loadPlaylists]);

  useEffect(() => {
    if (!selectedPlaylist) return;
    setForm({
      title: selectedPlaylist.title || "",
      condition: selectedPlaylist.condition || "",
      body_area: selectedPlaylist.body_area || "",
      injury_area: selectedPlaylist.injury_area || "",
      level: selectedPlaylist.level || "Basic",
      description: selectedPlaylist.description || "",
      is_active: selectedPlaylist.is_active !== false,
    });
    setExerciseId("");
  }, [selectedPlaylist]);

  const createPlaylist = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/admin/playlists/with-exercises", { ...cleanPayload(form), exercise_ids: [exerciseId] });
      toast.success("Playlist created");
      setPlaylists((current) => [data, ...current]);
      setSelectedPlaylistId(data.id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const savePlaylist = async () => {
    if (!selectedPlaylist) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/playlists/${selectedPlaylist.id}`, cleanPayload(form));
      toast.success("Playlist saved");
      setPlaylists((current) => current.map((playlist) => (playlist.id === data.id ? data : playlist)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const archivePlaylist = async () => {
    if (!selectedPlaylist || !window.confirm("Archive this playlist?")) return;
    try {
      const { data } = await api.post(`/admin/playlists/${selectedPlaylist.id}/archive`);
      toast.success("Playlist archived");
      setPlaylists((current) => current.map((playlist) => (playlist.id === data.id ? data : playlist)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const deletePlaylist = async () => {
    if (!selectedPlaylist || !window.confirm(`Delete playlist "${selectedPlaylist.title}"? Its exercises and patient programs will remain.`)) return;
    try {
      await api.delete(`/admin/playlists/${selectedPlaylist.id}`);
      const remaining = playlists.filter((playlist) => playlist.id !== selectedPlaylist.id);
      setPlaylists(remaining);
      setSelectedPlaylistId(remaining[0]?.id || "");
      toast.success("Playlist deleted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const addExercise = async () => {
    if (!selectedPlaylist || !exerciseId) return;
    try {
      const { data } = await api.post(`/admin/playlists/${selectedPlaylist.id}/exercises`, { exercise_id: exerciseId });
      toast.success("Exercise added");
      setPlaylists((current) => current.map((playlist) => (playlist.id === data.id ? data : playlist)));
      setExerciseId("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const removeExercise = async (playlistExerciseId) => {
    if (!selectedPlaylist || !window.confirm("Remove this exercise from the playlist?")) return;
    try {
      const { data } = await api.delete(`/admin/playlists/${selectedPlaylist.id}/exercises/${playlistExerciseId}`);
      toast.success("Exercise removed");
      setPlaylists((current) => current.map((playlist) => (playlist.id === data.id ? data : playlist)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const moveExercise = async (index, direction) => {
    if (!selectedPlaylist) return;
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= selectedPlaylist.exercises.length) return;
    const reordered = [...selectedPlaylist.exercises];
    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
    try {
      const { data } = await api.post(`/admin/playlists/${selectedPlaylist.id}/exercises/reorder`, {
        ordered_ids: reordered.map((row) => row.id),
      });
      setPlaylists((current) => current.map((playlist) => (playlist.id === data.id ? data : playlist)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Unable to reorder exercises.");
    }
  };

  return (
    <MobileShell contentClassName={adminShellClassName(displayPreference)}>
      <header className="px-6 pt-8 pb-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <button onClick={() => navigate(-1)} className="text-slate-500 mb-3" data-testid="playlists-back-btn"><ArrowLeft size={20} /></button>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-600">Exercise playlists</p>
            <h1 className="text-2xl font-bold font-display text-slate-900 mt-1">Exercise Playlists</h1>
            <p className="text-sm text-slate-500 mt-1">Reusable clinician groups for fast plan building.</p>
          </div>
          <div className="flex flex-wrap gap-2"><AdminHomeButton /><AdminDisplayPreference value={displayPreference} onChange={setDisplayPreference} /></div>
        </div>
      </header>

      <section className="px-6 pb-8 grid gap-4 lg:grid-cols-[340px_minmax(0,1fr)_360px] lg:items-start">
        <aside className="space-y-4 lg:sticky lg:top-4">
          <div className="grid gap-3">
            <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Search playlists" />
            <Select value={filters.condition} onValueChange={(value) => setFilters({ ...filters, condition: value })}>
              <SelectTrigger className="h-11 rounded-xl"><SelectValue placeholder="Condition" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All conditions</SelectItem>
                {(options.condition || []).map((condition) => <SelectItem key={condition.key} value={condition.name}>{condition.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.level} onValueChange={(value) => setFilters({ ...filters, level: value })}>
              <SelectTrigger className="h-11 rounded-xl"><SelectValue placeholder="Level" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All levels</SelectItem>
                {LEVELS.map((level) => <SelectItem key={level} value={level}>{level}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {playlists.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 p-4">
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-2">Existing playlists</div>
              <div className="space-y-2">
                {playlists.map((playlist) => (
                  <button
                    key={playlist.id}
                    onClick={() => setSelectedPlaylistId(playlist.id)}
                    className={`w-full text-left rounded-xl border p-3 ${playlist.id === selectedPlaylistId ? "border-teal-300 bg-teal-50" : "border-slate-100 bg-white"}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-slate-900">{playlist.title}</span>
                      <span className="text-xs text-slate-500">{playlist.level}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">{playlist.condition || "No condition"} - {playlist.exercises?.length || 0} exercises</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </aside>

        <main className="bg-white rounded-2xl border border-slate-100 p-4 space-y-3">
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Playlist details</div>
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Playlist title" />
          <p className="text-sm text-slate-500">Body region, injury area and condition are optional.</p>
          <div className="grid gap-3 sm:grid-cols-3">
            <OptionSelect kind="condition" value={form.condition} onChange={(condition) => setForm({ ...form, condition })} options={options} />
            <Select value={form.level} onValueChange={(value) => setForm({ ...form, level: value })}>
              <SelectTrigger className="h-11 rounded-xl"><SelectValue placeholder="Level" /></SelectTrigger>
              <SelectContent>
                {LEVELS.map((level) => <SelectItem key={level} value={level}>{level}</SelectItem>)}
              </SelectContent>
            </Select>
            <OptionSelect kind="body_region" value={form.body_area} onChange={(body_area) => setForm({ ...form, body_area })} options={options} />
          </div>
          <OptionSelect kind="injury_area" value={form.injury_area} onChange={(injury_area) => setForm({ ...form, injury_area })} options={options} />
          {!selectedPlaylist && <Select value={exerciseId} onValueChange={setExerciseId}><SelectTrigger className="h-11 rounded-xl"><SelectValue placeholder="Choose first exercise (required)" /></SelectTrigger><SelectContent>{exercises.map((exercise) => <SelectItem key={exercise.id} value={exercise.id}>{exercise.title}</SelectItem>)}</SelectContent></Select>}
          <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" rows={2} />
          <div className="flex flex-wrap gap-2">
            <Button onClick={selectedPlaylist ? savePlaylist : createPlaylist} disabled={saving || !form.title.trim() || (!selectedPlaylist && !exerciseId)} size="sm">
              {selectedPlaylist ? "Save playlist" : "Create playlist"}
            </Button>
            <Button onClick={() => { setSelectedPlaylistId(""); setExerciseId(""); setForm(getEmptyPlaylistForm()); }} variant="outline" size="sm">
              <Plus size={16} /> New
            </Button>
            {selectedPlaylist && selectedPlaylist.is_active !== false && (
              <Button onClick={archivePlaylist} variant="destructive" size="sm">
                <Archive size={16} /> Archive
              </Button>
            )}
            {selectedPlaylist && user?.role === "admin" && <Button onClick={deletePlaylist} variant="destructive" size="sm"><Trash2 size={16} /> Delete</Button>}
          </div>
        </main>

        {selectedPlaylist && (
          <aside className="bg-white rounded-2xl border border-slate-100 p-4 space-y-3 lg:sticky lg:top-4">
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Playlist exercises</div>
            <ExerciseSharingButtons playlistId={selectedPlaylist.id} disabled={!selectedPlaylist.exercises?.length || selectedPlaylist.is_active === false} />
            <div className="flex flex-col gap-2 sm:flex-row">
              <Select value={exerciseId} onValueChange={setExerciseId}>
                <SelectTrigger className="h-11 rounded-xl"><SelectValue placeholder="Choose exercise" /></SelectTrigger>
                <SelectContent>
                  {filteredExercises.map((exercise) => <SelectItem key={exercise.id} value={exercise.id}>{exercise.title}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button onClick={addExercise} disabled={!exerciseId} size="sm">Add exercise</Button>
            </div>
            <div className="space-y-2">
              {(selectedPlaylist.exercises || []).map((row, index) => (
                <div key={row.id} className="rounded-xl border border-slate-100 bg-slate-50 p-3 flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{row.exercise?.title || "Exercise"}</div>
                    <div className="text-xs text-slate-500 mt-1">{row.exercise?.sets || "-"} sets - {row.exercise?.reps || "-"} reps</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" disabled={index === 0} onClick={() => moveExercise(index, -1)} aria-label="Move exercise up"><ChevronUp size={16} /></Button>
                    <Button variant="ghost" size="sm" disabled={index === selectedPlaylist.exercises.length - 1} onClick={() => moveExercise(index, 1)} aria-label="Move exercise down"><ChevronDown size={16} /></Button>
                    <Button variant="outline" size="sm" onClick={() => removeExercise(row.id)}>Remove</Button>
                  </div>
                </div>
              ))}
              {selectedPlaylist.exercises?.length === 0 && (
                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">No exercises added yet.</div>
              )}
            </div>
          </aside>
        )}
      </section>
    </MobileShell>
  );
}

function getEmptyPlaylistForm() {
  return {
    title: "",
    condition: "",
    body_area: "",
    injury_area: "",
    level: "Basic",
    description: "",
    is_active: true,
  };
}

function cleanPayload(form) {
  return {
    title: textValue(form.title),
    condition: textValue(form.condition),
    body_area: textValue(form.body_area),
    injury_area: textValue(form.injury_area),
    level: textValue(form.level) || "Basic",
    description: textValue(form.description),
    is_active: form.is_active !== false,
  };
}
